// src/pages/InjectionKey.tsx
import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactPaginate from "react-paginate";

import {
  // IconFileImport, // 엑셀 업로드 메뉴 숨김
  // IconFileExport, // 등록/반품 샘플 메뉴 숨김
  IconFileExcel,
  IconPlus,
  IconChevronRight,
  IconChevronLeft,
  IconSearch,
  IconDownload,
  IconTrash,
  IconFile,
  IconRefresh,
} from "@tabler/icons-react";
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Menu,
  Select,
  Table,
  TableThead,
  TextInput,
  Badge,
  Text,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useModal } from "../contexts/useModal";
import {
  ErrorAlert,
  TerminalsExcelImport,
  TerminalView,
  TerminalDelete,
  TerminalUpdate,
} from "../components/Modal";
import { setActionLog, INT_MAX, formatDiagnosticError } from "../utils/util";
import { useDynamicScroll } from "../utils/useDynamicScroll";
import { formatMacDisplay } from "./TerminalAddHistory";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { ROW_HOVER_BG, BUTTON_WORD } from "../constants/colors"; // UI action presets
import {
  brand,
  dangerActionProps,
  labels,
  primaryActionProps,
  searchActionProps,
  SHOW_ALIAS,
} from "../constants/uiActions";
import { useCanWrite } from "../utils/useCanWrite";

const Terminals: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const [data, setData] = useState<any[]>([]);
  const [tab, setTab] = useState("ALL");
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  //const [showModal, setShowModal] = useState(false);

  const [total, setTotal] = useState(0);
  const [length, setLength] = useState(20);
  const [pageNo, setPageNo] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);

  const _isSearch = useRef(false);
  const [isSearch, setIsSearch] = useState(false);
  const todayStr = dayjs().format("YYYY-MM-DD"); // KST 기준 오늘 날짜
  const [startDate, setStartDate] = useState<string>(todayStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [allModels, setAllModels] = useState<any[]>([]);
  const [internalModelName, setInternalModelName] = useState("");
  const [externalModelName, setExternalModelName] = useState("");
  const [vanType, setVanType] = useState("");
  const [searchSpSerialNoStart, setSearchSpSerialNoStart] = useState("");
  const [searchSpSerialNoEnd, setSearchSpSerialNoEnd] = useState("");
  const [searchMacAddressStart, setSearchMacAddressStart] = useState("");
  const [searchMacAddressEnd, setSearchMacAddressEnd] = useState("");
  const [searchDikStart, setSearchDikStart] = useState("");
  const [searchDikEnd, setSearchDikEnd] = useState("");
  const [spSerialInjectFilter, setSpSerialInjectFilter] = useState("");
  const [macInjectFilter, setMacInjectFilter] = useState("");
  const [dikInjectFilter, setDikInjectFilter] = useState("");
  const [searchTerminalColor, setSearchTerminalColor] = useState("");
  const [searchAliasName, setSearchAliasName] = useState("");
  // const [selectSerialCode, setSelectSerialCode] = useState("025QHSC01");
  const [internalModelOptions, setInternalModelOptions] = useState<
    { value: string; label: string }[]
  >([{ value: "", label: "전체" }]);
  const [externalModelOptions, setExternalModelOptions] = useState<
    { value: string; label: string }[]
  >([{ value: "", label: "전체" }]);
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const { showModal } = useModal();
  const lock1 = useRef(false);
  const lock2 = useRef(false);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [importGroupId, setImportGroupId] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<number>(0);

  // 동적 스크롤 제어 (최소 높이 500px)
  useDynamicScroll({ minHeight: 500 });

  // Excel import progress polling (persists after modal close)
  useEffect(() => {
    let polling: number | null = null;
    const startPolling = (groupId: string) => {
      if (polling) return;
      polling = window.setInterval(async () => {
        try {
          const resp = await axiosInstance.get(
            `/api/v1/terminal/imports/${groupId}`
          );
          const d = resp.data?.data || {};
          const percent = d.progress ?? 1;
          setImportProgress(percent);
          if (d.status === "FAILED") {
            const { displayMsg } = formatDiagnosticError(
              d,
              "이전 단말기 등록 작업이 실패했습니다."
            );
            toast.error(displayMsg);
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch {}
            setImportGroupId(null);
            setImportProgress(0);
            if (polling) {
              clearInterval(polling);
              polling = null;
            }
            return;
          }
          if (percent >= 100) {
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch {}
            setImportGroupId(null);
            setImportProgress(100);
            if (polling) {
              clearInterval(polling);
              polling = null;
            }
          }
        } catch {
          try {
            localStorage.removeItem("terminalImportGroupId");
          } catch {}
          setImportGroupId(null);
          setImportProgress(0);
          if (polling) {
            clearInterval(polling);
            polling = null;
          }
        }
      }, 1000);
    };

    const existing = (() => {
      try {
        return localStorage.getItem("terminalImportGroupId");
      } catch {
        return null;
      }
    })();
    if (existing) {
      setImportGroupId(existing);
      startPolling(existing);
    }

    const probe = window.setInterval(() => {
      const gid = (() => {
        try {
          return localStorage.getItem("terminalImportGroupId");
        } catch {
          return null;
        }
      })();
      if (gid && gid !== importGroupId) {
        setImportGroupId(gid);
        startPolling(gid);
      }
    }, 1500);

    return () => {
      if (polling) clearInterval(polling);
      clearInterval(probe);
    };
  }, [importGroupId]);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/terminal/model/list", {
        params: { currentPageNo: 1, recordsPerPage: 1000 },
      })
      .then((res) => {
        const list: any[] = res.data?.data?.list ?? [];
        setAllModels(list);
      })
      .catch((err) => {
        console.error("단말기 모델 목록 로드 실패", err);
      });

    axiosInstance
      .get("/api/v1/common/van")
      .then((res) => {
        const mapped = (res.data?.data ?? []).map((item: any) => ({
          value: item.code,
          label: item.name,
        }));

        const withDefault = [{ value: "", label: "전체" }, ...mapped];
        setVanTypeOptions(withDefault);
        if (withDefault.length > 0) {
          setVanType(withDefault[0].value);
        }
      })
      .catch((err) => {
        console.error("VAN 목록 로드 실패", err);
        const defaultOptions = [{ value: "", label: "전체" }];
        setVanTypeOptions(defaultOptions);
        setVanType(defaultOptions[0].value);
      });
  }, []);

  useEffect(() => {
    const internalSet = new Set<string>();
    allModels.forEach((m) => {
      if (m.internalModelName && m.internalModelName.trim()) {
        internalSet.add(m.internalModelName.trim());
      }
    });
    const internalList = Array.from(internalSet).sort();
    setInternalModelOptions([
      { value: "", label: "전체" },
      ...internalList.map((name) => ({ value: name, label: name })),
    ]);
  }, [allModels]);

  useEffect(() => {
    const filtered = internalModelName
      ? allModels.filter(
          (m) => (m.internalModelName ?? "").trim() === internalModelName
        )
      : allModels;
    const externalSet = new Set<string>();
    filtered.forEach((m) => {
      if (m.externalModelName && m.externalModelName.trim()) {
        externalSet.add(m.externalModelName.trim());
      }
    });
    const externalList = Array.from(externalSet).sort();
    setExternalModelOptions([
      { value: "", label: "전체" },
      ...externalList.map((name) => ({ value: name, label: name })),
    ]);

    if (externalModelName && !externalSet.has(externalModelName)) {
      setExternalModelName("");
    }
  }, [allModels, internalModelName]);

  useEffect(() => {
    if (!lock1.current) {
      lock1.current = true;
      return;
    }
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      getList();
    }
  }, [length]);

  useEffect(() => {
    if (!lock2.current) {
      lock2.current = true;
      return;
    }
    getList();
  }, [pageNo]);

  useEffect(() => {
    getList();
  }, []);

  const handleResetFilters = () => {
    setSearchSpSerialNoStart("");
    setSearchSpSerialNoEnd("");
    setSearchMacAddressStart("");
    setSearchMacAddressEnd("");
    setSearchDikStart("");
    setSearchDikEnd("");
    setSpSerialInjectFilter("");
    setMacInjectFilter("");
    setDikInjectFilter("");
    setInternalModelName("");
    setExternalModelName("");
    setVanType("");
    setSearchTerminalColor("");
    setSearchAliasName("");
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      setTimeout(() => getList(), 0);
    }
  };

  /** S/N 범위 스마트 보정 (영문 접두사 + 숫자부 결합 등) */
  const normalizeSpSerialRange = (rawStart: string, rawEnd: string) => {
    let start = rawStart.trim().toUpperCase();
    let end = rawEnd.trim().toUpperCase();

    // 시작이 비어있고 종료만 있는 경우 -> 단일 검색으로 간주
    if (!start && end) {
      start = end;
      end = "";
    }

    if (!start && !end) return { start: undefined, end: undefined };
    if (!end) return { start: start || undefined, end: undefined };

    const startMatch = start.match(/^(.*?)(\d+)$/);
    const endMatch = end.match(/^(.*?)(\d+)$/);

    if (startMatch) {
      const startPrefix = startMatch[1];
      const startDigits = startMatch[2];

      if (!endMatch) {
        return { start, end };
      }

      const endPrefix = endMatch[1];
      const endDigits = endMatch[2];

      // 종료에 영문 접두사가 없거나 순수 숫자일 때
      if (!endPrefix && startPrefix) {
        if (endDigits.length <= startDigits.length) {
          const prefixDigits = startDigits.slice(0, startDigits.length - endDigits.length);
          const resolvedEndDigits = prefixDigits + endDigits;
          return { start, end: startPrefix + resolvedEndDigits };
        } else {
          return { start, end: startPrefix + endDigits };
        }
      }
    }

    return { start: start || undefined, end: end || undefined };
  };

  /** MAC 범위 스마트 보정 (10진수 숫자값, 16진수 6자리, 12자리 풀 MAC 등) */
  const normalizeMacAddressRange = (rawStart: string, rawEnd: string) => {
    let start = rawStart.trim().toUpperCase().replace(/[: -]/g, "");
    let end = rawEnd.trim().toUpperCase().replace(/[: -]/g, "");

    // 시작이 비어있고 종료만 있는 경우 -> 단일 검색으로 간주
    if (!start && end) {
      start = end;
      end = "";
    }

    if (!start && !end) return { start: undefined, end: undefined };
    if (!end) {
      if (/^\d+$/.test(start) && Number(start) > 0 && start.length <= 8) {
        const hex = Number(start).toString(16).toUpperCase().padStart(6, "0");
        return { start: hex, end: undefined };
      }
      return { start: start || undefined, end: undefined };
    }

    // 둘 다 10진수 숫자값 (테이블 괄호 안의 MAC-2 값, 예: 724795 ~ 724800)
    if (/^\d+$/.test(start) && /^\d+$/.test(end) && Number(start) > 0 && Number(end) > 0 && start.length <= 8 && end.length <= 8) {
      const hexStart = Number(start).toString(16).toUpperCase().padStart(6, "0");
      const hexEnd = Number(end).toString(16).toUpperCase().padStart(6, "0");
      return { start: hexStart, end: hexEnd };
    }

    // 시작이 12자리 풀 MAC인 경우
    if (start.length === 12) {
      const oui = start.slice(0, 6);
      if (/^\d+$/.test(end) && Number(end) > 0 && end.length <= 8) {
        const hexEnd = Number(end).toString(16).toUpperCase().padStart(6, "0");
        return { start, end: oui + hexEnd };
      }
      if (end.length === 6) {
        return { start, end: oui + end };
      }
    }

    // 시작이 6자리 HEX이고 종료가 10진수인 경우
    if (start.length === 6 && /^\d+$/.test(end) && Number(end) > 0 && end.length <= 8) {
      const hexEnd = Number(end).toString(16).toUpperCase().padStart(6, "0");
      return { start, end: hexEnd };
    }

    return { start: start || undefined, end: end || undefined };
  };

  /** DIK 범위 스마트 보정 */
  const normalizeDikRange = (rawStart: string, rawEnd: string) => {
    let start = rawStart.trim();
    let end = rawEnd.trim();

    // 시작이 비어있고 종료만 있는 경우 -> 단일 검색으로 간주
    if (!start && end) {
      start = end;
      end = "";
    }

    if (!start && !end) return { start: undefined, end: undefined };
    if (!end) return { start: start || undefined, end: undefined };

    if (/^\d+$/.test(start) && /^\d+$/.test(end)) {
      if (start.length > end.length) {
        end = end.padStart(start.length, "0");
      }
    }

    return { start: start || undefined, end: end || undefined };
  };

  const getList = () => {
    if (_isSearch.current) return;
    _isSearch.current = true;
    setIsSearch(true);

    const snRange = normalizeSpSerialRange(searchSpSerialNoStart, searchSpSerialNoEnd);
    const macRange = normalizeMacAddressRange(searchMacAddressStart, searchMacAddressEnd);
    const dikRange = normalizeDikRange(searchDikStart, searchDikEnd);

    axiosInstance
      .get("/api/v1/terminal/list", {
        params: {
          currentPageNo: pageNo,
          recordsPerPage: length,
          ...(internalModelName ? { internalModelName } : {}),
          ...(externalModelName ? { externalModelName } : {}),
          ...(vanType ? { vanType } : {}),
          ...(searchTerminalColor ? { terminalColor: searchTerminalColor } : {}),
          spSerialNoStart: snRange.start,
          spSerialNoEnd: snRange.end,
          macAddressStart: macRange.start,
          macAddressEnd: macRange.end,
          dikStart: dikRange.start,
          dikEnd: dikRange.end,
          spSerialInjectionYn: spSerialInjectFilter || undefined,
          macInjectionYn: macInjectFilter || undefined,
          refInjectionYn: dikInjectFilter || undefined,
          aliasName: searchAliasName.trim() || undefined,
        },
      })
      .then((res) => {
        if (res.status === 200) {
          const data = res.data as any;
          const r = data.data;
          const p = r.paging;
          setData(r.list || []);
          setTotal(p.totalRecordCount);
          setPageTotal(Math.ceil(p.totalRecordCount / length));
        } else {
          setData([]);
        }
      })
      .catch((err) => {
        console.error("단말기 리스트 로드 실패", err);
        // 인터셉터가 401 → refresh → 실패 → 로그인 이동 처리함
      })
      .finally(() => {
        _isSearch.current = false;
        setIsSearch(false);
      });
  };

  type TerminalSampleExportType = "register" | "incoming";

  const handleExport = async (type: TerminalSampleExportType) => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 업로드 샘플 다운로드 버튼 클릭",
    });
    try {
      const url =
        type === "register"
          ? "/files/register_sample.xlsx"
          : "/files/incoming_sample.xlsx";
      const timestamp = dayjs().format("YYYYMMDDHHmmss");
      const downloadName =
        type === "register"
          ? `등록_샘플_${timestamp}.xlsx`
          : `반품_샘플_${timestamp}.xlsx`;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      await setActionLog({
        eventType: "EXPORT",
        title: "단말기 업로드 샘플 다운로드 성공",
      });
    } catch (err: any) {
      await setActionLog({
        eventType: "EXPORT",
        title: "단말기 업로드 샘플 다운로드 실패",
        content: err,
      });
      toast.error("엑셀 다운로드에 실패하였습니다.");
    }
  };

  const handleZipReaderDownload = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "zipReader 다운로드 버튼 클릭",
    });
    try {
      const link = document.createElement("a");
      link.href = "/files/zipReader.zip";
      link.setAttribute("download", "zipReader.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      await setActionLog({
        eventType: "EXPORT",
        title: "zipReader 다운로드 성공",
      });
      toast.success("zipReader.zip 다운로드가 시작되었습니다.");
    } catch (err: any) {
      await setActionLog({
        eventType: "EXPORT",
        title: "zipReader 다운로드 실패",
        content: err,
      });
      toast.error("다운로드에 실패하였습니다.");
    }
  };

  const handleExcelImport = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 정보 업로드 버튼 클릭",
    });
    const flag = await showModal({
      component: TerminalsExcelImport,
      props: { initialTab: "excel", initialMode: "register" },
    });
    if (flag) {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 성공",
      });
      toast.success("업로드가 완료되었습니다.");
      getList();
    }
  };

  const handleDirectRegister = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 직접 등록 버튼 클릭",
    });
    const flag = await showModal({
      component: TerminalsExcelImport,
      props: { initialTab: "typing", initialMode: "register" },
    });
    if (flag) {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 직접 등록 성공",
      });
      toast.success("등록이 완료되었습니다.");
      getList();
    }
  };

  const handleExcelExport = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 리스트 엑셀 내보내기 버튼 클릭",
    });
    try {
      setIsSearch(true);

      const snRange = normalizeSpSerialRange(searchSpSerialNoStart, searchSpSerialNoEnd);
      const macRange = normalizeMacAddressRange(searchMacAddressStart, searchMacAddressEnd);
      const dikRange = normalizeDikRange(searchDikStart, searchDikEnd);

      // 페이징 없이 모든 데이터 조회
      const response = await axiosInstance.get("/api/v1/terminal/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          ...(internalModelName ? { internalModelName } : {}),
          ...(externalModelName ? { externalModelName } : {}),
          ...(vanType ? { vanType } : {}),
          spSerialNoStart: snRange.start,
          spSerialNoEnd: snRange.end,
          macAddressStart: macRange.start,
          macAddressEnd: macRange.end,
          dikStart: dikRange.start,
          dikEnd: dikRange.end,
          spSerialInjectionYn: spSerialInjectFilter || undefined,
          macInjectionYn: macInjectFilter || undefined,
          refInjectionYn: dikInjectFilter || undefined,
          aliasName: searchAliasName.trim() || undefined,
        },
      });

      if (response.status === 200) {
        const data = response.data as any;
        const terminalList = data.data.list || [];

        // ExcelJS 워크북 생성
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("단말기 리스트");

        // 컬럼 정의
        worksheet.columns = [
          { header: "번호", key: "number", width: 8 },
          { header: "S/N", key: "spSerialNo", width: 20 },
          { header: "MAC", key: "macAddress", width: 20 },
          { header: "내부모델", key: "internalModelName", width: 18 },
          { header: "외부모델", key: "externalModelName", width: 18 },
          { header: "VAN", key: "vanType", width: 15 },
          { header: "색상", key: "terminalColor", width: 10 },
          { header: "S/N 주입", key: "spInjection", width: 12 },
          { header: "DIK 주입", key: "dikInjection", width: 12 },
          { header: "MAC 주입", key: "macInjection", width: 12 },
          { header: "등록날짜", key: "createdDate", width: 12 },
        ];

        // 헤더 스타일링
        worksheet.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });

        // 데이터 추가
        terminalList.forEach((row: any, index: number) => {
          const dataRow = worksheet.addRow({
            number: index + 1,
            spSerialNo: row.spSerialNo || "",
            macAddress: row.macAddress || "",
            internalModelName: row.internalModelName || "",
            externalModelName: row.externalModelName || "",
            vanType:
              vanTypeOptions.find((item) => item.value === row.vanType)
                ?.label || row.vanType,
            terminalColor: row.terminalColor || "",
            spInjection: row.spSerialInjectionYn === "Y" ? "주입" : "미주입",
            dikInjection:
              row.dikYn === "Y" ||
              Boolean((row.securityKeyRef1 ?? "").trim()) ||
              Boolean((row.securityKeyRef2 ?? "").trim())
                ? row.refInjectionYn === "Y"
                  ? "주입"
                  : "미주입"
                : "없음",
            macInjection: row.macInjectionYn === "Y" ? "주입" : "미주입",
            createdDate: row.createdDate
              ? dayjs(row.createdDate).format("YYYY-MM-DD")
              : "",
          });

          // 데이터 행 스타일링
          dataRow.eachCell((cell, colNumber) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = {
              top: { style: "thin", color: { argb: "FFD9D9D9" } },
              left: { style: "thin", color: { argb: "FFD9D9D9" } },
              bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
              right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };

            // 주입/미주입 상태에 따른 색상 적용
            const cellValue = cell.value;
            if (
              typeof cellValue === "string" &&
              (cellValue === "주입" ||
                cellValue === "미주입" ||
                cellValue === "없음")
            ) {
              const isNone = cellValue === "없음";
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {
                  argb: isNone
                    ? "FFF1F5F9"
                    : cellValue === "주입"
                      ? "FFD4EDDA"
                      : "FFF8D7DA",
                },
              };
              cell.font = {
                color: {
                  argb: isNone
                    ? "FF64748B"
                    : cellValue === "주입"
                      ? "FF155724"
                      : "FFA94442",
                },
                bold: true,
              };
            }
          });
        });

        // 파일 다운로드
        const fileName = `단말기_리스트_${dayjs().format(
          "YYYY-MM-DD_HH-mm-ss"
        )}.xlsx`;
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        await setActionLog({
          eventType: "EXPORT",
          title: "단말기 리스트 엑셀 내보내기 성공",
          content: `총 ${terminalList.length}건`,
        });
        toast.success(
          `엑셀 파일이 다운로드되었습니다. (${terminalList.length}건)`
        );
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "EXPORT",
        title: "단말기 리스트 엑셀 내보내기 실패",
        content: err,
      });
      toast.error("엑셀 내보내기에 실패하였습니다.");
      console.error("엑셀 내보내기 오류:", err);
    } finally {
      setIsSearch(false);
    }
  };

  const tabList = [
    { label: "전체", value: "ALL" },
    { label: "미주입", value: "1" },
    { label: "주입", value: "2" },
    { label: "출고", value: "3" },
    { label: "반품 미주입", value: "4" },
    { label: "반품 주입", value: "5" },
  ];

  const getInjectionStatus = (yn: string) =>
    yn === "Y" ? "주입" : "미주입";

  const renderInjectBadge = (yn: string) => {
    const status = getInjectionStatus(yn);
    return (
      <Badge
        style={{
          backgroundColor: status === "주입" ? "#d4edda" : "#f8d7da",
          color: status === "주입" ? "#155724" : "#a94442",
        }}
        variant="filled"
      >
        {status}
      </Badge>
    );
  };

  const isDikTarget = (row: {
    securityKeyRef1?: string | null;
    securityKeyRef2?: string | null;
    dikYn?: string | null;
  }) =>
    row.dikYn === "Y" ||
    Boolean(
      (row.securityKeyRef1 ?? "").trim() || (row.securityKeyRef2 ?? "").trim(),
    );

  const renderDikInjectBadge = (row: {
    securityKeyRef1?: string | null;
    securityKeyRef2?: string | null;
    refInjectionYn?: string | null;
    dikYn?: string | null;
  }) => {
    if (!isDikTarget(row)) {
      return "-";
    }
    return renderInjectBadge(row.refInjectionYn ?? "N");
  };

  /*
  const filtered = data.filter((row) => {
    const status = getKeyInjectionStatus(row.keyInjectionYn);
    const tabMatch = tab === "All" || status === tab;
    const searchMatch =
      search === "" ||
      String(row.terminalId).includes(search) ||
      String(row.serialNo).includes(search) ||
      String(row.deviceType).includes(search);
    return tabMatch && searchMatch;
  });

  const PAGE_SIZE = 15;

  const emptyRows =
    PAGE_SIZE - filtered.length > 0 ? PAGE_SIZE - filtered.length : 0;

  */

  const handleTerminalView = async (row: any) => {
    await showModal({ component: TerminalView, props: { row } });
  };

  const rows = data.map((row, idx) => {
    const isSelected = selectedRow === row.terminalId;
    const vanName =
      vanTypeOptions.find((item) => String(item.value) === String(row.vanType))
        ?.label || "-";
    const dikValue =
      row.securityKeyRef1 || (row.dikYn === "Y" ? "사용" : "-");

    const rawMac = (row.macAddress || "").replace(/[^0-9A-Fa-f]/g, "");
    const mac2Number = rawMac.length >= 12 ? parseInt(rawMac.slice(6), 16) : null;
    const formattedMac = row.macAddress
      ? `${formatMacDisplay(row.macAddress)}${mac2Number !== null && !isNaN(mac2Number) ? ` (${mac2Number})` : ""}`
      : "-";

    return (
      <Table.Tr
        key={row.terminalId}
        bg={
          isSelected
            ? "var(--mantine-color-blue-light)"
            : hoveredRow === idx
            ? "#f0f4fa"
            : undefined
        }
        fw={isSelected ? "700" : undefined}
        onMouseEnter={() => setHoveredRow(idx)}
        onMouseLeave={() => setHoveredRow(null)}
        onClick={() => {
          setSelectedRow((prev) =>
            prev === row.terminalId ? null : row.terminalId,
          );
          void handleTerminalView(row);
        }}
        style={{ cursor: "pointer" }}
      >
        <Table.Td p={"4px 12px"}>{row.terminalId}</Table.Td>
        <Table.Td p={"4px 12px"}>{row.spSerialNo || "-"}</Table.Td>
        <Table.Td p={"4px 12px"}>{formattedMac}</Table.Td>
        <Table.Td p={"4px 12px"}>{dikValue}</Table.Td>
        <Table.Td p={"4px 12px"}>
          {`${row.internalModelName || "-"} / ${row.externalModelName || "-"}`}
        </Table.Td>
        <Table.Td p={"4px 12px"}>
          {`${vanName} / ${row.terminalColor || "-"}`}
        </Table.Td>
        <Table.Td p={"4px 12px"}>
          {renderInjectBadge(row.spSerialInjectionYn)}
        </Table.Td>
        <Table.Td p={"4px 12px"}>
          {renderInjectBadge(row.macInjectionYn)}
        </Table.Td>
        <Table.Td p={"4px 12px"}>{renderDikInjectBadge(row)}</Table.Td>
        {SHOW_ALIAS && (
          <Table.Td p={"4px 12px"}>
            <div
              style={{
                maxWidth: 140,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={row.aliasName || ""}
            >
              {row.aliasName || "-"}
            </div>
          </Table.Td>
        )}
      </Table.Tr>
    );
  });

  return (
    <div
      style={{
        height: "100%",
        background: "#fafbfc",
        borderRadius: "12px",
        padding: "0.75rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        minHeight: "500px", // 최소 높이 보장
      }}
    >
      <LoadingOverlay
        visible={isSearch}
        zIndex={1000}
        overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
        loaderProps={{ color: "pink", type: "bars" }}
      />
      <Flex align={"center"} justify={"space-between"} pb={"1rem"}>
        <Box fz={"1.75rem"} fw={700}>
          {menuTitle}
        </Box>
        <Flex gap={"xs"}>
          <Menu
            transitionProps={{ transition: "pop-top-right" }}
            position="bottom-end"
            width={200}
            withinPortal
            radius="sm"
          >
            <Menu.Target>
              <Button
                leftSection={
                  <IconPlus
                    size={20}
                    color="#fff"
                    stroke={1.5}
                  />
                }
                {...primaryActionProps}
                disabled={!canWrite}
              >
                {labels.register}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{labels.register}</Menu.Label>
              {/* 사용 중지: 엑셀 업로드
              <Menu.Item
                leftSection={
                  <IconFileImport size={20} color="#708D75" stroke={1.5} />
                }
                onClick={handleExcelImport}
              >
                엑셀 업로드
              </Menu.Item>
              */}
              <Menu.Item
                leftSection={
                  <IconPlus size={20} color="#0F766E" stroke={1.8} />
                }
                onClick={handleDirectRegister}
              >
                직접 등록
              </Menu.Item>
              <Menu.Divider />
              <Menu.Label>보조 기능</Menu.Label>
              {/* 사용 중지: 등록 샘플 / 반품 샘플
              <Menu.Item
                leftSection={
                  <IconFileExport size={20} color="#F59E0B" stroke={1.5} />
                }
                onClick={() => handleExport("register")}
              >
                등록 샘플
              </Menu.Item>

              <Menu.Item
                leftSection={
                  <IconFileExport size={20} color="#F59E0B" stroke={1.5} />
                }
                onClick={() => handleExport("incoming")}
              >
                반품 샘플
              </Menu.Item>
              */}
              <Menu.Item
                leftSection={
                  <IconFile size={20} color="#9B59B6" stroke={1.5} />
                }
                onClick={() => void handleZipReaderDownload()}
              >
                zipReader
              </Menu.Item>
              <Menu.Item
                leftSection={
                  <IconDownload size={20} color={brand.tool} stroke={1.5} />
                }
                onClick={handleExcelExport}
              >
                목록 내보내기
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Flex>

      <Flex direction="column" gap="xs" pb="0.75rem">
        {/* 1행: S/N, MAC, DIK 단일 및 범위 검색 */}
        <Flex align="flex-end" gap="md" wrap="wrap">
          <Flex direction="column" gap={2}>
            <Text size="xs" fw={500} c="dimmed">S/N (단일 / 범위)</Text>
            <Flex align="center" gap={4}>
              <TextInput
                placeholder="시작 (단일)"
                value={searchSpSerialNoStart}
                onChange={(e) => setSearchSpSerialNoStart(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (pageNo !== 1) setPageNo(1);
                    else getList();
                  }
                }}
                style={{ width: 145 }}
              />
              <span style={{ color: "#868e96" }}>~</span>
              <TextInput
                placeholder="종료"
                value={searchSpSerialNoEnd}
                onChange={(e) => setSearchSpSerialNoEnd(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (pageNo !== 1) setPageNo(1);
                    else getList();
                  }
                }}
                style={{ width: 145 }}
              />
            </Flex>
          </Flex>

          <Flex direction="column" gap={2}>
            <Text size="xs" fw={500} c="dimmed">MAC (단일 / 범위)</Text>
            <Flex align="center" gap={4}>
              <TextInput
                placeholder="시작 (단일/숫자)"
                value={searchMacAddressStart}
                onChange={(e) => setSearchMacAddressStart(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (pageNo !== 1) setPageNo(1);
                    else getList();
                  }
                }}
                style={{ width: 145 }}
              />
              <span style={{ color: "#868e96" }}>~</span>
              <TextInput
                placeholder="종료"
                value={searchMacAddressEnd}
                onChange={(e) => setSearchMacAddressEnd(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (pageNo !== 1) setPageNo(1);
                    else getList();
                  }
                }}
                style={{ width: 145 }}
              />
            </Flex>
          </Flex>

          <Flex direction="column" gap={2}>
            <Text size="xs" fw={500} c="dimmed">DIK (단일 / 범위)</Text>
            <Flex align="center" gap={4}>
              <TextInput
                placeholder="시작 (단일)"
                value={searchDikStart}
                onChange={(e) => setSearchDikStart(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (pageNo !== 1) setPageNo(1);
                    else getList();
                  }
                }}
                style={{ width: 145 }}
              />
              <span style={{ color: "#868e96" }}>~</span>
              <TextInput
                placeholder="종료"
                value={searchDikEnd}
                onChange={(e) => setSearchDikEnd(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (pageNo !== 1) setPageNo(1);
                    else getList();
                  }
                }}
                style={{ width: 145 }}
              />
            </Flex>
          </Flex>
        </Flex>

        {/* 2행: 모델, VAN, 색상, 별칭, 주입 여부(S/N, MAC, DIK) 및 버튼들 */}
        <Flex align="flex-end" gap="xs" wrap="wrap">
          <Select
            label="내부모델"
            data={internalModelOptions}
            value={internalModelName}
            allowDeselect={false}
            onChange={(v) => setInternalModelName(v || "")}
            style={{ width: 120 }}
          />
          <Select
            label="외부모델"
            data={externalModelOptions}
            value={externalModelName}
            allowDeselect={false}
            onChange={(v) => setExternalModelName(v || "")}
            style={{ width: 120 }}
          />
          <Select
            label="VAN"
            data={vanTypeOptions}
            value={vanType}
            allowDeselect={false}
            onChange={(v) => setVanType(v || "")}
            style={{ width: 100 }}
          />
          <Select
            label="색상"
            data={[
              { value: "", label: "전체" },
              { value: "BLACK", label: "BLACK" },
              { value: "WHITE", label: "WHITE" },
            ]}
            value={searchTerminalColor}
            allowDeselect={false}
            onChange={(v) => setSearchTerminalColor(v || "")}
            style={{ width: 90 }}
          />
          {SHOW_ALIAS && (
            <TextInput
              label="별칭"
              placeholder="별칭 입력"
              value={searchAliasName}
              onChange={(e) => setSearchAliasName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (pageNo !== 1) setPageNo(1);
                  else getList();
                }
              }}
              style={{ width: 120 }}
            />
          )}
          <Select
            label="S/N 주입"
            data={[
              { value: "", label: "전체" },
              { value: "Y", label: "주입" },
              { value: "N", label: "미주입" },
            ]}
            value={spSerialInjectFilter}
            allowDeselect={false}
            onChange={(v) => setSpSerialInjectFilter(v || "")}
            style={{ width: 95 }}
          />
          <Select
            label="MAC 주입"
            data={[
              { value: "", label: "전체" },
              { value: "Y", label: "주입" },
              { value: "N", label: "미주입" },
            ]}
            value={macInjectFilter}
            allowDeselect={false}
            onChange={(v) => setMacInjectFilter(v || "")}
            style={{ width: 95 }}
          />
          <Select
            label="DIK 주입"
            data={[
              { value: "", label: "전체" },
              { value: "Y", label: "주입" },
              { value: "N", label: "미주입" },
              { value: "NONE", label: "해당없음(-)" },
            ]}
            value={dikInjectFilter}
            allowDeselect={false}
            onChange={(v) => setDikInjectFilter(v || "")}
            style={{ width: 115 }}
          />
          <Button
            leftSection={<IconSearch size={18} stroke={1.5} />}
            onClick={() => {
              if (pageNo !== 1) {
                setPageNo(1);
              } else {
                getList();
              }
            }}
            disabled={isSearch}
            {...searchActionProps}
          >
            {labels.search}
          </Button>
          <Button
            variant="default"
            leftSection={<IconRefresh size={18} stroke={1.5} />}
            onClick={handleResetFilters}
            style={{ color: "#495057" }}
          >
            초기화
          </Button>
          <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
            <Button
              leftSection={<IconTrash size={20} stroke={1.5} />}
              onClick={async () => {
                const flag = await showModal({
                  component: TerminalDelete,
                  props: {},
                });
                if (flag) {
                  getList();
                }
              }}
              disabled={!canWrite}
              {...dangerActionProps}
            >
              {labels.delete}
            </Button>
          </div>
        </Flex>
      </Flex>
      {/* Floating progress widget for Excel import */}
      {importGroupId && importProgress > 0 && importProgress < 100 && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            padding: "10px 12px",
            zIndex: 1000,
            minWidth: 220,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
            엑셀 Import 진행중
          </div>
          <div style={{ fontSize: 12, color: "#555" }}>
            그룹: {importGroupId}
          </div>
          <div
            style={{
              marginTop: 6,
              height: 8,
              background: "#eef2f7",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.max(5, Math.min(100, importProgress))}%`,
                height: "100%",
                background: brand.tool,
              }}
            />
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#333" }}>
            {Math.floor(importProgress)}%
          </div>
        </div>
      )}

      {/* 탭 사용하지 않음 (대부분 '전체'만 사용하므로 주석 처리)
      <div style={{ display: "flex", alignItems: "center" }}>
        {tabList.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            style={{
              background: tab === t.value ? "#fff" : "transparent",
              border: "none",
              borderBottom:
                tab === t.value ? `2px solid ${brand.accent}` : "2px solid transparent",
              color: "#222b45",
              fontWeight: tab === t.value ? "bold" : "normal",
              fontSize: 16,
              marginRight: 24,
              padding: "8px 0",
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      */}

      <div
        style={{
          flex: 1, // 남은 공간을 모두 차지
          minHeight: "300px", // 최소 높이 보장
          overflow: "auto",
          scrollbarGutter: "stable",
          boxSizing: "border-box",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          borderRadius: 8,
          background: "#fff",
          position: "relative",
        }}
      >
        <Table withTableBorder withColumnBorders highlightOnHover>
          <Table.Thead>
            <Table.Tr bg={"#f5f6fa"} ta={"left"} pos={"sticky"} top={0}>
              <Table.Th p={12} pos={"sticky"} top={0}>
                번호
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                S/N
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                MAC
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                DIK
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                모델(내부/외부)
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                VAN/색상
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                S/N 주입
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                MAC 주입
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                DIK 주입
              </Table.Th>
              {SHOW_ALIAS && (
                <Table.Th p={12} pos={"sticky"} top={0}>
                  별칭
                </Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows}
            {/*Array.from({ length: 30 }).map((_, i) => (
              <Table.Tr key={i} style={{ height: 30,}}>
                <td colSpan={5}>asd</td>
              </Table.Tr>
            ))*/}
          </Table.Tbody>
        </Table>
      </div>
      <Flex pt={16} align={"flex-end"}>
        <Flex mr={"auto"} align={"center"}>
          <span style={{ marginRight: "1rem" }}>총 {total}개</span>
          <select
            onChange={(e) => {
              const opt_list = [20, 50, 100];
              setLength(opt_list[e.target.options.selectedIndex]);
              //getPageCount()
            }}
            style={{
              height: "31px",
              width: "50px",
              textAlign: "center",
              fontSize: "1rem",
            }}
          >
            <option>20</option>
            <option>50</option>
            <option>100</option>
          </select>
          <span>개씩 보기</span>
        </Flex>
        <ReactPaginate
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
          pageCount={pageTotal}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(e) => {
            setPageNo(e.selected + 1);
          }}
          forcePage={pageNo - 1}
          containerClassName="pagination"
          activeClassName="active"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          nextClassName="page-item"
          previousLinkClassName="page-link"
          nextLinkClassName="page-link"
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
      </Flex>
    </div>
  );
};

export default Terminals;
